/**
 * ticket-inapp.fonts — the card's typefaces, carried BY the card into
 * whatever document mounts it, so a host needs no font wiring of its own for
 * the credential to render as designed.
 *
 * WHY THE CARD CARRIES ITS OWN FONT RATHER THAN NAMING ONE THE HOST LOADS:
 * a family the card merely NAMES resolves to whatever that document happens
 * to have loaded, and a host that never loaded it silently falls back to a
 * system sans — the wordmark stops being the wordmark, at no error and no
 * failed build. That is not hypothetical: the display face this package's
 * stencil composition rests on is loaded by no host by default, so every
 * surface that mounted the card needed its own `@fontsource` import to get
 * correct typography, and a surface that forgot got a fallback face. A
 * per-host wiring step that must be remembered is not a dependency the card
 * can rely on; embedding is, because it cannot be forgotten.
 *
 * INJECTED ONCE PER DOCUMENT, NOT PER CARD: the faces below are base64 data
 * URIs (`ticket-file.fonts.ts`, the same asset the rasterized file path
 * already ships), which is tens of kilobytes — a `<style>` block emitted
 * inside each card's own markup, the way `ticketCardGrounds` emits its small
 * layout rules, would repeat that payload once per card and a picker renders
 * several at once. `ensureTicketCardFonts` instead appends one id-guarded
 * `<style>` to `document.head` the first time any card mounts and returns
 * immediately every time after, so N cards cost one copy. `@font-face` is
 * document-scoped rather than element-scoped in any case, so the head is
 * where it actually belongs.
 *
 * THE FAMILY NAMES ARE PACKAGE-PRIVATE, NOT THE UPSTREAM ONES: a face
 * registered as `Anton` or `Poppins` would collide with a host that loads
 * its own copy of the same family at a different weight set or version, and
 * whichever registered last would win for the whole document — the card
 * would be silently restyling its host's UI, or being restyled by it. The
 * prefixed names below are claimed by this package alone, so the card's
 * typography is decided entirely by the card and neither leaks outward nor
 * admits an override.
 *
 * A HOST THAT NEVER RUNS SCRIPT still gets the right face: the rasterized
 * file path composes `TICKET_FILE_FONT_CSS` into its own page directly
 * (`ticket-file.page.ts`) rather than calling this function, because that
 * page is assembled as a string and screenshotted with no card lifecycle to
 * hook.
 */
import { TICKET_FILE_FONT_CSS } from "./ticket-file.fonts.ts";

/** The display face the stencil composition's wordmark is drawn in
 *  — upstream Anton, re-labelled (see this file's own header for why the
 *  upstream name is not reused). */
export const TICKET_CARD_DISPLAY_FAMILY = "TktCardDisplay";

/** The text face every label and value on every variant reads in — upstream
 *  Poppins, re-labelled on the same reasoning. */
export const TICKET_CARD_TEXT_FAMILY = "TktCardText";

const STYLE_ELEMENT_ID = "tkt-card-fonts";

/** Splits the embedded stylesheet into its individual `@font-face` rules and
 *  re-labels each one's family to this package's own. Deriving the rules from
 *  the single embedded source, rather than pasting a second copy of the same
 *  base64 beside it, keeps one copy of each face in the repo — two copies
 *  would be free to drift the moment either is regenerated, and the drift
 *  would show up as the on-screen card and the emailed file disagreeing
 *  about the same typeface. */
function scopedFontFaceCss(): string {
	return TICKET_FILE_FONT_CSS.split("@font-face")
		.filter((chunk) => chunk.trim().length > 0)
		.map((chunk) => {
			const rule = `@font-face${chunk}`;
			if (rule.includes("font-family:'Anton'")) {
				return rule.replace(
					"font-family:'Anton'",
					`font-family:'${TICKET_CARD_DISPLAY_FAMILY}'`,
				);
			}
			if (rule.includes("font-family:'Poppins'")) {
				return rule.replace(
					"font-family:'Poppins'",
					`font-family:'${TICKET_CARD_TEXT_FAMILY}'`,
				);
			}
			return rule;
		})
		.join("");
}

/**
 * The document's Font Loading API, or `null` where the host has none.
 *
 * THE CARD MOUNTS WITHOUT IT: `document.fonts` is absent in happy-dom and
 * jsdom — the environments this repo's component suites run in — and absent
 * in any non-browser host. Reading through it unguarded turns a missing
 * progressive-enhancement API into a `TypeError` thrown from
 * `connectedCallback`, which takes down every test that merely mounts
 * something rendering this card, and would take down the same mount in any
 * such host at runtime. The API only ever sharpens WHEN the fit correction
 * re-runs; the correction itself measures real layout and needs nothing from
 * it. Absent, the card degrades to the fallback face and the single immediate
 * correction `updated()` already performs — visibly less exact, never broken,
 * and never a throw.
 *
 * Every access to the API in this package goes through here, so the guard is
 * one decision made once rather than an optional chain each caller has to
 * remember.
 */
export function ticketCardFontSet(): FontFaceSet | null {
	if (typeof document === "undefined") return null;
	return (document as Document & { fonts?: FontFaceSet }).fonts ?? null;
}

/** Registers the card's faces on the current document, once. Safe to call on
 *  every card's every render — the id guard makes every call after the first
 *  a lookup. A non-browser caller (a jsdom-less unit test importing this
 *  module for its family-name constants) is a no-op rather than a throw. */
export function ensureTicketCardFonts(): void {
	if (typeof document === "undefined") return;
	if (document.getElementById(STYLE_ELEMENT_ID)) return;
	const style = document.createElement("style");
	style.id = STYLE_ELEMENT_ID;
	style.textContent = scopedFontFaceCss();
	document.head.appendChild(style);
}

/** The same rules as a string, for a caller composing a page as text rather
 *  than mounting into a live document. */
export function ticketCardFontFaceCss(): string {
	return scopedFontFaceCss();
}
